// ============================================================================
// Local-storage backed vitality recommendations cache (24h TTL).
// Keyed per user; carries the signalsHash so the hook can compare against the
// server signature and decide whether to refresh. Also exposes pub/sub for
// invalidation triggers (new facts, new chat sessions, favorites changes).
// ============================================================================

import { CachedVitality, VitalityRecommendations } from '../types/vitality';

const STORAGE_PREFIX = 'vitality:';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const _listeners = new Map<string, Set<() => void>>();

function _key(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export function loadCache(userId: string): CachedVitality | null {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(_key(userId));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CachedVitality;
    if (!entry?.payload || !entry.signalsHash) return null;
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
      window.localStorage.removeItem(_key(userId));
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function saveCache(
  userId: string,
  payload: VitalityRecommendations
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const entry: CachedVitality = {
      signalsHash: payload.signalsHash,
      payload,
      cachedAt: Date.now()
    };
    window.localStorage.setItem(_key(userId), JSON.stringify(entry));
  } catch {
    // ignore quota errors
  }
}

export function clearCache(userId: string): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(_key(userId));
  } catch {
    /* swallow */
  }
}

/**
 * Subscribe to invalidation notifications for a given user. Caller is
 * responsible for triggering invalidation via `notifyInvalidation` from the
 * relevant context mutations (facts, sessions, favorites).
 *
 * Returns an unsubscribe function.
 */
export function subscribeToInvalidations(
  userId: string,
  onInvalidate: () => void
): () => void {
  if (!userId) return () => {};
  let set = _listeners.get(userId);
  if (!set) {
    set = new Set();
    _listeners.set(userId, set);
  }
  set.add(onInvalidate);
  return () => {
    const s = _listeners.get(userId);
    if (!s) return;
    s.delete(onInvalidate);
    if (s.size === 0) _listeners.delete(userId);
  };
}

export function notifyInvalidation(userId: string): void {
  if (!userId) return;
  clearCache(userId);
  const set = _listeners.get(userId);
  if (!set) return;
  set.forEach(fn => {
    try {
      fn();
    } catch {
      /* swallow */
    }
  });
}
